import { Component, Input, OnInit, Optional, TemplateRef } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import {
  ActionType, AnalysisType,
  AppComponentName, Comparator, DeviceCameraInfo, DeviceInfo,
  Event, EventAction, EventType, LogicOperator,
  MediaActionsOptionsModel,
  Message,
  MessageActionType,
  MessageNoteCategory,
  MessageType, MotionCondition, Sensor, SensorCondition,
  SensorDataInfo,
  SensorGroup,
  SensorReadingType, TimerCondition, TimerEventType, TimingInterval,
  UserGroup, WaterSchedule
} from '@models';

import moment from 'moment';
import { TriggerService } from '../trigger.service';
import { EventMapper } from '../trigger.mapping';
import { AppMessageService } from '@services/message.service';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { CommonShared } from 'app/shared/classes/common';
import { ChangeLogNotesParams, ChangeLogNotesComponent } from 'app/components/messages/changelog-notes/changelognotes.components';
import { trim } from 'lodash';
import { SensorService } from '@services/sensor.service';
import { ActionQueueService } from '@services/actionQueue.service';
import { Action } from 'rxjs/internal/scheduler/Action';
import { ActionQueue } from '@models/ActionQueue/ActionQueue.model';
import { Authorization } from 'app/shared/classes/authorization';

const zeroPad = (num: number, places: number) => String(num).padStart(places, '0');

export interface EditEventsAndActionsModalParams {
  events: Event[];
  event: Event;
  changeLogNotes: string;
  username: string;
  selectedUserGroup: UserGroup;
  preEditObjectAsJSONString: string;
  eventTitle: string;
  associatedUserGroups: UserGroup[];
  evaluateTimes: string[];
  sensorDataTypes: SensorReadingType[];
  sensorGroups: SensorGroup[];
  IrrDestinationDDOptions: string[];
  CamGatewayDDOptions: string[];
  IrrZoneDDOptions: string[];
  globalWaterSchedules: WaterSchedule[];
  buttonValue: string;
  devices: DeviceInfo[];
  stopEventAction: any;
  userGroups: UserGroup[];
  actionQueues: ActionQueue[];
}

@Component({
  standalone: false,
  selector: 'app-edit-events-and-actions-modal',
  templateUrl: './edit-events-and-actions-modal.component.html',
  styleUrls: ['./edit-events-and-actions-modal.component.scss']
})

export class EditEventsAndActionsModalComponent implements OnInit, EditEventsAndActionsModalParams {

  common = new CommonShared();
  auth = new Authorization();
  
  modalDragOn: boolean = false;

  @Input() events: Event[];
  @Input() event: Event;
  eventAction: EventAction;
  @Input() changeLogNotes: string;
  @Input() username: string;
  @Input() selectedUserGroup: UserGroup;
  @Input() preEditObjectAsJSONString: string;
  @Input() eventTitle: string;
  @Input() associatedUserGroups: UserGroup[];
  @Input() evaluateTimes: string[];
  @Input() sensorDataTypes: SensorReadingType[];
  @Input() sensorGroups: SensorGroup[];
  TimingSingleDDOptions: string[] = [];
  @Input() IrrDestinationDDOptions: string[];
  @Input() CamGatewayDDOptions: string[];
  @Input() IrrZoneDDOptions: string[];
  @Input() globalWaterSchedules: WaterSchedule[];
  @Input() buttonValue: string;
  @Input() devices: DeviceInfo[];
  @Input() stopEventAction: any;
  @Input() userGroups: UserGroup[];
  duplicateName = false;
  eventMapper = new EventMapper();
  @Input() actionQueues: ActionQueue[] = [];
  selectedActionQueue: ActionQueue = new ActionQueue();

  // Inline-embed mode (e.g. the Create wizard's right pane): hide the modal
  // chrome/footer and don't self-save. Defaults false so modal callers are unchanged.
  @Input() embedded = false;

  public userGroupconfig = {
    displayKey: 'name',
    search: true,
    height: '300px',
    placeholder: 'Select User Group',
    noResultsFound: 'No User Groups found!',
    searchOnKey: 'name',
    limitTo: 0,
    customComparator: (item)=> item
  };

  public EvaluateTimeGroupConfig = {
    displayKey: 'name',
    search: false,
    height: '200px',
    placeholder: 'Hour',
    noResultsFound: '',
    searchOnKey: 'name',
    limitTo: 0
  };

  public SearchDDConfig = {
    displayKey: 'name',
    search: true,
    height: '200px',
    placeholder: 'Select',
    noResultsFound: '',
    searchOnKey: 'name',
    limitTo: 200,
    customComparator: (item)=> item
  };

  public DataTypesDDConfig = {
    displayKey: 'type',
    search: true,
    height: '200px',
    placeholder: '',
    noResultsFound: '',
    searchOnKey: 'type',
    limitTo: 200,
    customComparator: (item)=> item
  };

  public NoSearchDDConfig: any = {
    displayKey: 'name',
    search: true,
    height: '200px',
    placeholder: 'Select',
    noResultsFound: '',
    searchOnKey: 'name',
    limitTo: 200,
    customComparator: (item)=> item
  };

  public actionQueueConfig = {
    displayKey: 'name',
    search: true,
    height: '300px',
    placeholder: 'Select Action Queue',
    noResultsFound: 'No Action Queues found!',
    searchOnKey: 'name',
    limitTo: 0,
    customComparator: (item)=> item
  };

  public AnalysisTypeDDOptions: string[] = [ 'median', 'average', 'min', 'max', 'data rate', '|medA-medB|', '|avgA-avgB|', '|minA-minB|', '|maxA-maxB|', 'group count', 'single count', 'trimmed mean' ];
  public ComparatorDDOptions: string[] = [ '<=', '>=', '<', '>', '=', '!=' ];
  public LogicOperatorDDOptions: string[] = [ 'And', 'Or' ];
  public TimerTypeDDOptions: string[] = [ 'Single', 'Repeat' ];
  public TimingRepeatDDOptions: string[] = [ 'Every Half Hour', 'Every Hour', 'Every 2 Hours' , 'Every 4 Hours', 'Every 8 Hours' ];
  public ActionTypeDDOptions: string[] = [ 'Email', 'Url', 'Task', 'Irrigation', 'FloodIrrigation', 'AppMessage', 'Camera' ];
  public CaptureResDDOptions: string[] = [ 'Low', 'Medium', 'High' ];
  public MotionDetectionAreaOptions: string[] = [ 'Full Image' ];
  public CameraDDOptions: string[] = [];
  public CameraModeDDOptions: string[] = [ 'image', 'video' ];

  private logModalRef: BsModalRef;
  private actionOptionsRef: BsModalRef;

  constructor(@Optional() public bsModalRef: BsModalRef, private toastr: ToastrService, private modalService: BsModalService, private _sensorService: SensorService,
              private _triggerService: TriggerService, private _appMessageService: AppMessageService) { }

  
  ngOnInit(): void {
    this.buildTimingSingleDDOptions();
    this.event.eventTypeSelect = this.getSensorBasedEventMode(this.event);

    this.event.eventActions.forEach(x => {
      var cameraName = x.cameraName;
      var cameraId = x.cameraId;
      this.updateSelectedGateway(x);
      x.cameraName = cameraName;
      x.cameraId = cameraId;
      this.updateSelectedCamera(x);

      if (x.mediaActionOptions != undefined) {
        x.mediaActionOptions.forEach(y => {
          if (y.CameraMode == undefined || y.CameraMode == '') y.CameraMode = "image";}
        );
      }
    });

    this.selectedActionQueue = this.actionQueues.find(x => x.id === this.event.actionQueueId);
  }

  
  setDragOn(drag: boolean) {
    this.modalDragOn = drag;

    if (drag) {
      setTimeout(() => {
        this.modalDragOn = false;
      },3000);
    }
  }


  buildTimingSingleDDOptions(): void {
    this.TimingSingleDDOptions = [];
    for (let hour = 0; hour < 24; hour++) {
      this.TimingSingleDDOptions.push ( zeroPad(hour,2) + ':15' );
      this.TimingSingleDDOptions.push ( zeroPad(hour,2) + ':45' );
    }
  }


  addNotes2ChangeLog() {
    this.changeLogNotes = '';

    const params: ChangeLogNotesParams = {
      changeLogNotes: this.changeLogNotes,
      userGroupId: this.selectedUserGroup.id,
      category: MessageNoteCategory.EventEdit
    };

    var modalRef = this.modalService.show(ChangeLogNotesComponent, { animated: false, backdrop: false, ignoreBackdropClick: true, class: 'modal-above-confirmdialog', initialState: params });
    modalRef.content.onChangeLogNotes.subscribe((value) => {
      this.changeLogNotes = value;
    });
  }

  
  delEventAction(actionIndex: number): void {
    this.event.eventActions.splice(actionIndex, 1);
    this.event.eventActions = [...this.event.eventActions];
  }

  addEventAction() {
    const triggerAction = this.newEventAction();
    this.event.eventActions.push(triggerAction);
  }

  duplicateNamecheck(name: string): boolean {
    this.duplicateName = false;
    this.events.forEach(e => {
      if (e.name.toUpperCase() == name.toUpperCase() && e.id != this.event.id){
        this.toastr.error('Event name is already in use');
        this.duplicateName = true;
        return this.duplicateName;
      }
    });
    return this.duplicateName;
  }


  updateEvent(): void {
    // When embedded inline (wizard), the host owns save; never self-save/hide.
    if (this.embedded) {
      return;
    }
    if (this.duplicateNamecheck(this.event.name)) {
      return;
    }

    if (this.event.id < 1) {
      this.onCreateEvent(this.event);
    } else {
      this.onEditEvent(this.event);
    }
  }


  onEventEnable(value: any) {
    this.event.isDisabled = !value.checked;
    this.event.isDisabledColor = this.getEnabledColor(this.event.isDisabled);
  }


  updateSelectedGateway(eventAction: EventAction) {
    this.event.eventActions.forEach(x => {
      if (x.deviceName != '') {
        var device = this.devices.find(d => d.name == x.deviceName);
        if (device != undefined) {
          x.deviceSerial = device.serialNumber;
        }
      }
    });

    this.updateCameraDDOptions(eventAction);

    eventAction.cameraName = "";
    eventAction.cameraId = 0;
  }


  updateCameraDDOptions(eventAction: EventAction) {
    this.CameraDDOptions = [];
    var selectedGateway = this.devices.find(x => x.name == eventAction.deviceName);
    if (selectedGateway != undefined) {
      this.CameraDDOptions = selectedGateway.deviceCameras.map(x => x.name);
    }
  }


  updateSelectedCamera(eventAction: EventAction) {
    var gatewayDevice = this.devices.find(x => x.serialNumber == eventAction.deviceSerial);

    if (gatewayDevice != undefined && gatewayDevice.deviceCameras != undefined) {
      var selectedCameraInfo = gatewayDevice.deviceCameras.find(x => x.name == eventAction.cameraName);

      if (selectedCameraInfo == undefined && gatewayDevice.deviceCameras.length > 0) {
        selectedCameraInfo = gatewayDevice.deviceCameras[0];
      }

      if (selectedCameraInfo != undefined) {
        eventAction.cameraId = selectedCameraInfo.id;
        eventAction.cameraModel = selectedCameraInfo.model;

        if (!selectedCameraInfo.model.includes('Reolink')) {
          eventAction.cameraPresets = "1";
        }
      }

    }
  }


  updateCameraGatewaySerial() {
    this.event.motionConditions.forEach(a => {
      if (a.deviceName != '') {
        a.deviceSerial = this.devices.find(d => d.name == a.deviceName).serialNumber;
      }
    });
  }


  createEmptyDeviceCameraInfo(): DeviceCameraInfo {
    var cameraInfo: DeviceCameraInfo = {
      id: 0,
      deviceId: 0,
      name: '',
      type: '',
      model: '',
      port: '',
      login: '',
      presets: [],
      settings: {}
    };
    return cameraInfo;
  }


  addTimerCondition() {
    const timerEvent = this.newTimerCondition();
    this.event.timerConditions.push(timerEvent);
    this.updateOrdering(this.event.timerConditions);
  }


  delTimerCondition(eventIndex: number) {
    this.event.timerConditions.splice(eventIndex, 1);
    this.event.timerConditions = [...this.event.timerConditions];
    this.updateOrdering(this.event.timerConditions);
  }


  addSensorCondition() {
    const triggerSensorEvent = this.newSensorCondition();
    this.event.sensorConditions.push(triggerSensorEvent);
    this.updateOrdering(this.event.sensorConditions);
  }


  delSensorCondition(eventIndex: number) {
    this.event.sensorConditions.splice(eventIndex, 1);
    this.event.sensorConditions = [...this.event.sensorConditions];
    this.updateOrdering(this.event.sensorConditions);
  }


  addMotionCondition() {
    const triggerMotionEvent = this.newMotionCondition();
    this.event.motionConditions.push(triggerMotionEvent);
    this.updateOrdering(this.event.motionConditions);
  }


  delMotionCondition(eventIndex: number) {
    this.event.motionConditions.splice(eventIndex, 1);
    this.event.motionConditions = [...this.event.motionConditions];
    this.updateOrdering(this.event.motionConditions);
  }


  private newSensorCondition(): SensorCondition {
    const sensorGroupName = (this.sensorGroups.length > 0 ? this.sensorGroups[0].name : null);
    const sensorGroupId = (this.sensorGroups.length > 0 ? this.sensorGroups[0].id : null);
    const readingType = undefined;

    const newEvent: SensorCondition = {
      id: 0,
      eventID: this.event.id,
      sensorGroupID: undefined,
      sensorGroup: undefined,
      sensorReadingType: undefined,
      numberOfSamples: 0,
      lastXMinutes: 0,
      analysisType: AnalysisType.median,
      comparator: Comparator.lt,
      threshold: 0,
      logicOperator: LogicOperator.And,
      order: 0
    };
    return newEvent;
  }


  private newMotionCondition(): MotionCondition {
    const sensorGroupName = (this.sensorGroups.length > 0 ? this.sensorGroups[0].name : null);
    const sensorGroupId = (this.sensorGroups.length > 0 ? this.sensorGroups[0].id : null);
    const readingType = undefined;

    const newEvent: MotionCondition = {
      id: 0,
      eventID: this.event.id,
      deviceSerial: '',
      deviceName: '',
      sensitivity: 80,
      detectionArea: 'Full Image',
      postMotionSleepTime: 60,
      preMotionCaptureTime: 0,
      logicOperator: LogicOperator.And,
      order: 0
    };
    return newEvent;
  }


  private sensorGroupSelectionChanged(sensorGroup: SensorGroup, sensorCondition: SensorCondition) {
    sensorCondition.sensorReadingType = undefined;
    this.sensorDataTypes = this.getReadingTypesForSensorGroup(sensorGroup);
  }


  private openSensorDataTypesDropDown(sensorGroup: SensorGroup) {
    this.sensorDataTypes = this.getReadingTypesForSensorGroup(sensorGroup);
  }


  private getReadingTypesForSensorGroup(sensorGroup: SensorGroup): SensorReadingType[] {
    var sensorReadingTypes: SensorReadingType[] = [];

    if (sensorGroup == undefined || sensorGroup.sensors == undefined || sensorGroup.sensors.length < 1) {
      return sensorReadingTypes;
    }

    sensorGroup.sensors.forEach(x => {
      x.sensorReadingTypes.forEach(y => {
        if (sensorReadingTypes.findIndex(z => z.id == y.id) < 0) {
          var readingType: SensorReadingType = {
            id: y.id,
            type: y.type,
            checked: false
          }
          sensorReadingTypes.push(readingType);
        }
      });
    });

    return sensorReadingTypes;
  }


  private onCreateEvent(event: Event): void {
    this.updateEventUserInfo(event);

    if (!this.checkMinimumEventRequirements(event)) {
      return;
    }

    if (this.bsModalRef != null) {
      this.bsModalRef.hide();
    }

    this._triggerService.createEvent(this.eventMapper.mapEvent2EventInfo(event)).subscribe(
      results => {
        if (results != null) {
          this.createMessageData(MessageActionType.New, event, '');
          this.toastr.success('Success');
        } else {
          this.toastr.warning('Error with creating event. Reload page.');
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while adding trigger. Reload page.');
      });
  }


  private onEditEvent(event: Event): void {
    this.updateEventUserInfo(event);

    if (!this.checkMinimumEventRequirements(event)) {
      return;
    }

    if (this.bsModalRef != null) {
      this.bsModalRef.hide();
    }

    const eventInfo = this.eventMapper.mapEvent2EventInfo(event);
    eventInfo.modifiedDate = moment().utc().toDate();

    this._triggerService.updateEvent(eventInfo, 'Updating event ...', this.username).subscribe(
      results => {
        if (results != null) {
          this.createMessageData(MessageActionType.Edited, event, this.getEditChanges(event));
          this.toastr.success('Success');
        } else {
          this.toastr.warning('Error saving edits. Reload page.');
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while updating event. Reload page.');
      });
  }


  private updateEventUserInfo(event: Event) {
    if (this.selectedUserGroup != null && this.selectedUserGroup.id > -1) {
      event.userGroupID = this.selectedUserGroup.id;
      event.userGroup = this.selectedUserGroup.name;
    } else {
      event.userGroupID = null;
      event.userGroup = this.username;
    }
  }


  motionSensitivityCheck(motion: MotionCondition) {
    if (motion.sensitivity < 1) motion.sensitivity = 1;
    if (motion.sensitivity > 100) motion.sensitivity = 100;
  }


  motionSleepAfterCheck(motion: MotionCondition) {
    if (motion.postMotionSleepTime < 1) motion.sensitivity = 1;
  }


  private checkMinimumEventRequirements(event: Event): boolean {
    let verified = true;

    if (event.name == undefined) {
      this.toastr.error('Please provide a name for the event');
      verified = false;
    }

    if (event.eventType == "Motion Based") {
      event.motionConditions.forEach(x => {
        if (x.deviceName == '') {
          this.toastr.error('Need to select a gateway device');
          verified = false;
        }
        if (x.sensitivity < 1 || x.sensitivity > 100) {
          this.toastr.error('Motion sensitivity range is 1-100');
          verified = false;
        }
        if (x.postMotionSleepTime < 1) {
          this.toastr.error('Motion sleep time must be greater than 0');
          verified = false;
        }
      });
    }

    if (event.eventType == "Sensor Based") {
      event.sensorConditions.forEach(x => {
        if (x.sensorGroup == undefined || x.sensorGroup.id == undefined) {
          this.toastr.error('Need to select a sensor group');
          verified = false;
        }
        if (x.numberOfSamples < 1 && event.eventTypeSelect == "SensorValues") {
          this.toastr.error('Need to specify a number for #Val greater than 0');
          verified = false;
        }
        if (x.lastXMinutes < 1 && event.eventTypeSelect == "SensorMinutes") {
          this.toastr.error('Need to specify a number for #Min greater than 0');
          verified = false;
        }
        if (x.sensorReadingType == undefined || x.sensorReadingType.id == undefined) {
          this.toastr.error('Need to select a reading type.');
          verified = false;
        }
      });
    }

    event.eventActions.forEach(a => {
      switch (a.type) {
      case ActionType.Email:
        if (a.destination == undefined || a.destination == '') {
          this.toastr.error('Email: Please include an email address with your action');
          verified = false;
        }
        break;

      case ActionType.AppMessage:
        if (a.destination == undefined || a.destination == '') {
          this.toastr.error('Message: Please include a message with your action');
          verified = false;
        }
        break;

      case ActionType.Url:
        if (a.destination == undefined || a.destination == '') {
          this.toastr.error('URL: Please include a URL address with your action');
          verified = false;
        }
        break;

      case ActionType.Task:
        if (a.deviceName == undefined || a.deviceName == '') {
          this.toastr.error('Task: Please select a gateway device');
          verified = false;
        }
        if (a.taskAction == undefined || a.taskAction == '') {
          this.toastr.error('Task: Please specify a task for the gateway device');
          verified = false;
        }
        break;

      case ActionType.FloodIrrigation:
        if (a.deviceName == undefined || a.deviceName == '') {
          this.toastr.error('Irrigation: Please select a gateway device');
          verified = false;
        }
        if (a.sourceZone == undefined) {
          this.toastr.error('Irrigation: Please select a source zone for flood irrigation');
          verified = false;
        }
        if (a.fillZone == undefined) {
          this.toastr.error('Irrigation: Please select a fill zone for flood irrigation');
          verified = false;
        }
        if (a.drainZone == undefined) {
          this.toastr.error('Irrigation: Please select a drain zone for flood irrigation');
          verified = false;
        }
        if (a.fillSchedule.name == undefined || a.fillSchedule.name == '') {
          this.toastr.error('Irrigation: Please select a fill schedule for the flood irrigation');
          verified = false;
        }
        if (a.drainSchedule.name == undefined || a.drainSchedule.name == '') {
          this.toastr.error('Irrigation: Please select a drain schedule for the flood irrigation');
          verified = false;
        }
        break;

      case ActionType.Irrigation:
        if (a.deviceName == undefined || a.deviceName == '') {
          this.toastr.error('Irrigation: Please select a gateway device');
          verified = false;
        }
        if (a.zone == undefined) {
          this.toastr.error('Irrigation: Please select a zone for the irrigation');
          verified = false;
        }
        if (a.schedule.name == undefined || a.schedule.name == '') {
          this.toastr.error('Irrigation: Please select an irrigation schedule');
          verified = false;
        }
        break;

      case ActionType.Camera:
        if (a.deviceName == undefined || a.deviceName == '') {
          this.toastr.error('Task: Please select a gateway device');
          verified = false;
        }
        if (a.cameraName == undefined || a.cameraName == '') {
          this.toastr.error('Task: Please select a camera');
          verified = false;
        }
        a.cameraPresets = trim(a.cameraPresets);
        if (a.cameraPresets == undefined || a.cameraPresets == '') {
          this.toastr.error('Task: Please specify one or more saved camera location/preset IDs');
          verified = false;
        }
        break;

      default:
        break;
      }
    });

    return verified;
  }


  private createMessageData(msgActionType: MessageActionType, event: Event, msgNote: string) {
    const message: Message = {
      id: 0,
      messageType: MessageType.Audit,
      appComponentName: AppComponentName.EventAndActions,
      actionType: msgActionType,
      appComponentItem: event.name,
      details: msgNote,
      userNotes: this.changeLogNotes,
      count: 1,
      acknowledged: false,
      userName: this.username,
      userGroupId: event.userGroupID
    };

    this._appMessageService.createAppMessage(message).subscribe(
      results => {
        if (results != null) {
          //this.toastr.success('Success');
        } else {
          //this.toastr.warning('Not found.');
        }
        return;
      },
      err => {
        //this.toastr.error('Error occurred while saving change log.');
      });
  }

  private getEditChanges(event: Event): string {
    let results = '';

    if (this.preEditObjectAsJSONString === '') {
      return results;
    }

    const original: Event = JSON.parse(this.preEditObjectAsJSONString);

    results += this.common.compareProperty('Name', original.name, event.name);
    results += this.common.compareProperty('Description', original.description, event.description);
    results += this.common.compareProperty('EventType', original.eventType, event.eventType);
    results += this.common.compareProperty('Enabled', this.common.prepareProperty4String(!original.isDisabled), this.common.prepareProperty4String(!event.isDisabled));
    results += this.common.compareProperty('EventStartTime', original.evaluateStartTime, event.evaluateStartTime);
    results += this.common.compareProperty('EventEndTime', original.evaluateEndTime, event.evaluateEndTime);

    const daysOfWeekOrig = this.eventMapper.convertDaysOfWeekBoolArray2String(original.evaluateDaysOfWeek);
    const daysOfWeekEvent = this.eventMapper.convertDaysOfWeekBoolArray2String(event.evaluateDaysOfWeek);
    results += this.common.compareProperty('EventDaysOfWeek', daysOfWeekOrig, daysOfWeekEvent);

    if (original.sensorConditions.length !== event.sensorConditions.length) {
      results += this.common.compareProperty('SensorConditionCount', original.sensorConditions.length.toString(), event.sensorConditions.length.toString());
    } else {
      for (let i=0; i<event.sensorConditions.length; i++) {
        results += this.common.compareProperty('SensorCond.' + i+1 + '.name', original.sensorConditions[i].sensorGroup.name, event.sensorConditions[i].sensorGroup.name);
        results += this.common.compareProperty('SensorCond.' + i+1 + '.type', original.sensorConditions[i].sensorReadingType.type, event.sensorConditions[i].sensorReadingType.type);
        results += this.common.compareProperty('SensorCond.' + i+1 + '.lastXSamples', this.common.prepareProperty4String(original.sensorConditions[i].numberOfSamples), this.common.prepareProperty4String(event.sensorConditions[i].numberOfSamples));
        results += this.common.compareProperty('SensorCond.' + i+1 + '.lastxMinutes', this.common.prepareProperty4String(original.sensorConditions[i].lastXMinutes), this.common.prepareProperty4String(event.sensorConditions[i].lastXMinutes));
        results += this.common.compareProperty('SensorCond.' + i+1 + '.analysis', original.sensorConditions[i].analysisType, event.sensorConditions[i].analysisType);
        results += this.common.compareProperty('SensorCond.' + i+1 + '.comp', original.sensorConditions[i].comparator, event.sensorConditions[i].comparator);
        results += this.common.compareProperty('SensorCond.' + i+1 + '.logic', original.sensorConditions[i].logicOperator, event.sensorConditions[i].logicOperator);

        const conditionResults = this.common.compareProperty('SensorCond.' + i+1 + '.threshold', this.common.prepareProperty4String(original.sensorConditions[i].threshold), this.common.prepareProperty4String(event.sensorConditions[i].threshold));
        results += conditionResults;
      }
    }

    if (original.timerConditions.length !== event.timerConditions.length) {
      results += this.common.compareProperty('TimerConditionCount', original.timerConditions.length.toString(), event.timerConditions.length.toString());
    } else {
      for (let i=0; i<event.timerConditions.length; i++) {
        const daysOfWeekOrig = this.eventMapper.convertDaysOfWeekBoolArray2String(original.timerConditions[i].daysOfWeek);
        const daysOfWeekEvent = this.eventMapper.convertDaysOfWeekBoolArray2String(event.timerConditions[i].daysOfWeek);
        results += this.common.compareProperty('TimerCond.' + i+1 + '.DaysOfWeek', daysOfWeekOrig, daysOfWeekEvent);
        results += this.common.compareProperty('TimerCond.' + i+1 + '.type', original.timerConditions[i].type, event.timerConditions[i].type);
        results += this.common.compareProperty('TimerCond.' + i+1 + '.single', original.timerConditions[i].timingSingle, event.timerConditions[i].timingSingle);
        results += this.common.compareProperty('TimerCond.' + i+1 + '.repeat', original.timerConditions[i].timingRepeat, event.timerConditions[i].timingRepeat);
        results += this.common.compareProperty('TimerCond.' + i+1 + '.logic', original.timerConditions[i].logicOperator, event.timerConditions[i].logicOperator);
      }
    }

    if (original.eventActions.length !== event.eventActions.length) {
      results += this.common.compareProperty('EventActionCount', original.eventActions.length.toString(), event.eventActions.length.toString());
    } else {
      for (let i=0; i<event.eventActions.length; i++) {
        results += this.common.compareProperty('EventAction.' + i+1 + '.type', original.eventActions[i].type, event.eventActions[i].type);
        results += this.common.compareProperty('EventAction.' + i+1 + '.destination', original.eventActions[i].destination, event.eventActions[i].destination);
        results += this.common.compareProperty('EventAction.' + i+1 + '.deviceSerial', original.eventActions[i].deviceSerial, event.eventActions[i].deviceSerial);
        results += this.common.compareProperty('EventAction.' + i+1 + '.deviceName', original.eventActions[i].deviceName, event.eventActions[i].deviceName);
        results += this.common.compareProperty('EventAction.' + i+1 + '.zone', this.common.prepareProperty4String(original.eventActions[i].zone), this.common.prepareProperty4String(event.eventActions[i].zone));
        results += this.common.compareProperty('EventAction.' + i+1 + '.schedule', original.eventActions[i].scheduleName, event.eventActions[i].scheduleName);
        results += this.common.compareProperty('EventAction.' + i+1 + '.taskAction', original.eventActions[i].taskAction, event.eventActions[i].taskAction);
        results += this.common.compareProperty('EventAction.' + i+1 + '.onError', original.eventActions[i].onError, event.eventActions[i].onError);
        results += this.common.compareProperty('EventAction.' + i+1 + '.actionOptions', this.common.prepareProperty4String(original.eventActions[i].irrigationActionOptions), this.common.prepareProperty4String(event.eventActions[i].irrigationActionOptions));
        results += this.common.compareProperty('EventAction.' + i+1 + '.notificationOptions', this.common.prepareProperty4String(original.eventActions[i].notificationSectionOptions), this.common.prepareProperty4String(event.eventActions[i].notificationSectionOptions));
      }
    }

    results += this.common.compareProperty('UserGroupID', this.common.prepareProperty4String(original.userGroupID), this.common.prepareProperty4String(event.userGroupID));

    return results;
  }


  private getEnabledColor(isDisabled: boolean): string {
    if (isDisabled == false) {
      return '#a9d18e';  //'#33FF33';
    } else {
      return 'white';
    }
  }

  private newEventAction(): EventAction {
    const newAction: EventAction = {
      id: 0,
      eventID: this.event.id,
      type: ActionType.Email,
      destination: '',
      deviceName: '',
      deviceSerial: '',
      zone: 20,
      scheduleName: '',
      scheduleID: -1,
      taskAction: '',
      onError: '',
      mediaResolution: 'Low',
      mediaDuration: 5,
      mediaDelay: 0,
      cameraId: 0,
      cameraName: '',
      cameraModel: '',
      cameraPresets: '',
      mediaActionOptions: [],
      sourceZone: 21,
      fillZone: 22,
      fillScheduleName: '',
      fillScheduleId: -1,
      drainZone: 23,
      drainScheduleName: '',
      drainScheduleId: -1,
      irrigationActionOptions: undefined,
      notificationSectionOptions: undefined
    };
    return newAction;
  }

  private newTimerCondition(): TimerCondition {
    const newEvent: TimerCondition = {
      id: 0,
      eventID: this.event.id,
      type: TimerEventType.Single,
      daysOfWeek: [ false, false, false, false, false, false, false ],
      timingSingle: '08:15',
      timingRepeat: TimingInterval.EveryTwoHours,
      logicOperator: LogicOperator.And,
      order: 0
    };
    return newEvent;
  }

  private updateOrdering(eventConditions: { order: number }[]): void {
    if (eventConditions == null) {
      return;
    }
    for (let order = 0; order < eventConditions.length; order++) {
      eventConditions[order].order = order;
    }
  }


  displayToolTipFor(referenceName: string): string {
    switch (referenceName) {
      case "lastXMinutes":
        return "Use sensor readings from the last 'x' minutes";
    
      case "lastXValues":
        return "Use the last 'x' sensor readings from the sensor group"

      default:
        return '';
    }
  }


  setSensorBasedEventMode(mode: string) {
    this.event.eventTypeSelect = mode;
   
    this.event.sensorConditions.forEach(x => {
      if (mode == "SensorValues") {
        x.numberOfSamples = (x.lastXMinutes != undefined && x.lastXMinutes > 0) ? x.lastXMinutes : 10;
        x.lastXMinutes = 0;
      }
      else if (mode == "SensorMinutes") {
        x.lastXMinutes = (x.numberOfSamples != undefined && x.numberOfSamples > 0) ? x.numberOfSamples : 60;
        x.numberOfSamples = 0;
      }
    });
  }


  getSensorBasedEventMode(event: Event): string {
    var mode = "SensorMinutes";

    if (event.eventType == EventType.SensorBased) {
      if (event.sensorConditions.length > 0 && event.sensorConditions[0].numberOfSamples > 0) {
        mode = "SensorValues";
      }
    }

    return mode;
  }


  openMediaActionOptions(action: EventAction, template: TemplateRef<any>) {
    if (action.mediaActionOptions == undefined) {
      action.mediaActionOptions = [];
    }

    var posIds = action.cameraPresets.split(' ');

    posIds.forEach(x => {   // add missing placeholders
      var existing = action.mediaActionOptions.findIndex(y => y.PosId == Number(x));

      if (existing < 0) {
        action.mediaActionOptions.push({
          PosId: Number(x),
          Tags: '',
          ExpireDays: 180,
          SleepAfter: 0,
          CameraMode: 'image',
          VideoDuration: 5
        });
      }
    });

    for (var i = 0; i < action.mediaActionOptions.length; i++) {
      var option = action.mediaActionOptions[i];
      if (!posIds.includes(option.PosId.toString())) {
        action.mediaActionOptions.splice(i, 1);
      }
    }

    this.eventAction = action;
    this.actionOptionsRef = this.modalService.show(template, { animated: false, backdrop: false, ignoreBackdropClick: true });
  }


  openIrrigationActionOptions(action: EventAction, template: TemplateRef<any>) {
    // if (action.irrigationActionOptions == undefined) {
    //   action.irrigationActionOptions = { UseQueue: false, ActionQueueId: -1 };
    // }

    this.eventAction = action;
    //this.selectedActionQueue = this.actionQueues.find(x => x.id === action.irrigationActionOptions.ActionQueueId);
    this.actionOptionsRef = this.modalService.show(template, { animated: false, backdrop: false, ignoreBackdropClick: true });
  }


  openNotificationActionOptions(action: EventAction, template: TemplateRef<any>) {
    if (action.notificationSectionOptions == undefined) {
      // An action whose options have never been saved sends the readings that met the
      // condition only, which is the notification body KIG sent before the two lists existed.
      action.notificationSectionOptions = { IncludeMatchedReadings: true, IncludeAllReadings: false };
    }

    this.eventAction = action;
    this.actionOptionsRef = this.modalService.show(template, { animated: false, backdrop: false, ignoreBackdropClick: true });
  }


  sortActionOptions(actionOptions: MediaActionsOptionsModel[]) {
    actionOptions.sort((a, b) => {
      if (b.PosId > a.PosId) {
        return -1;
      }
      if (b.PosId < a.PosId) {
        return 1;
      }
    });
  }


  resetTriggerButtonStatus(eventAction: EventAction) {
    this.stopEventAction(this.event);

    var createdDate = moment().utc().toDate();
    this.setZoneIrrigationStatusHigh(eventAction, createdDate);

    createdDate = moment(createdDate).add(10, 'milliseconds').toDate();
    this.setZoneIrrigationStatusLow(eventAction, createdDate);
  }


  setZoneIrrigationStatusHigh(eventAction: EventAction, createdDate: Date) {
    var zoneHighLevel = parseInt(`${eventAction.zone}`) + 0.8;
    var detailsHighLevel = parseInt(`${eventAction.zone}`) + 0.6;

    var uuid = eventAction.deviceSerial;
    var sensorData: SensorDataInfo = {
      uuid: eventAction.deviceSerial,
      tag: eventAction.deviceName,
      createdDate: createdDate,
      payload: `{"irrigate-zone-${eventAction.zone}":"${zoneHighLevel}", "irrigatedetails-zone-${eventAction.zone}":"${detailsHighLevel}"}`
    }

    this._sensorService.ingestSensorData(sensorData).subscribe(
      results => {
        if (results != null) {
         //this.toastr.success('Trigger status reset successfully');
        } else {
          //this.toastr.warning('Unable to reset trigger status');
        }
        return;
      },
      err => {
        //this.toastr.error('Error: Unable to reset trigger status');
      });
  }


  setZoneIrrigationStatusLow(eventAction: EventAction, createdDate: Date) {
    var zoneLowLevel = parseInt(`${eventAction.zone}`);
    var detailsLowLevel = parseInt(`${eventAction.zone}`);

    var uuid = eventAction.deviceSerial;
    var sensorData: SensorDataInfo = {
      uuid: eventAction.deviceSerial,
      tag: eventAction.deviceName,
      createdDate: createdDate,
      payload: `{"irrigate-zone-${eventAction.zone}":"${zoneLowLevel}", "irrigatedetails-zone-${eventAction.zone}":"${detailsLowLevel}"}`
    }

    this._sensorService.ingestSensorData(sensorData).subscribe(
      results => {
        if (results != null) {
         this.toastr.success('Trigger status reset successfully');
        } else {
          this.toastr.warning('Unable to reset trigger status');
        }
        return;
      },
      err => {
        this.toastr.error('Error: Unable to reset trigger status');
      });
  }


  onActionQueueChange() {
    this.event.actionQueueId = this.selectedActionQueue.id;
  }


  onUseActionQueueChange(state: boolean) {
    this.event.useActionQueue = state;
    this.event.actionQueueId = (state && this.selectedActionQueue != null) ? this.selectedActionQueue.id : 0;
  }


}
